/**
 * Theme management — light / dark / system, persisted to localStorage.
 *
 * Architecture:
 *   - User preference: 'light' | 'dark' | 'system' (default: 'system').
 *     'system' follows the OS via prefers-color-scheme and updates live.
 *   - Effective theme: 'light' | 'dark' — what actually renders. Resolved
 *     from preference + matchMedia, applied as the `dark` class on <html>.
 *   - Persistence: localStorage key `testops-theme`. Default for first-time
 *     users is 'system' (NOT light) so dark-mode OS users see dark theme.
 *   - Flash prevention: `initThemeFromStorage()` runs SYNCHRONOUSLY in
 *     main.tsx before React renders, so the initial paint already has the
 *     correct class on <html>.
 *   - Cross-tab sync: storage events propagate preference changes between
 *     tabs. matchMedia listener handles OS theme changes when in 'system'.
 */
import { useCallback, useEffect, useState } from 'react'

export type ThemePreference = 'light' | 'dark' | 'system'
export type EffectiveTheme = 'light' | 'dark'

const STORAGE_KEY = 'testops-theme'
const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)'

function readStoredPreference(): ThemePreference | null {
  if (typeof window === 'undefined') return null
  try {
    const v = window.localStorage.getItem(STORAGE_KEY)
    return v === 'light' || v === 'dark' || v === 'system' ? v : null
  } catch {
    return null
  }
}

function osPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia(DARK_MEDIA_QUERY).matches
}

function resolveEffective(pref: ThemePreference): EffectiveTheme {
  if (pref === 'system') return osPrefersDark() ? 'dark' : 'light'
  return pref
}

function applyToDOM(effective: EffectiveTheme) {
  const root = document.documentElement
  if (effective === 'dark') {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
}

/**
 * Apply the resolved theme to <html> before React first renders. Called
 * once from main.tsx. Without this, users see a flash of the wrong theme
 * on hard reload while React boots.
 *
 * Preference resolution: stored value > 'system' (which reads OS).
 */
export function initThemeFromStorage(): void {
  const stored = readStoredPreference()
  const pref: ThemePreference = stored ?? 'system'
  if (resolveEffective(pref) === 'dark') {
    document.documentElement.classList.add('dark')
  }
}

/**
 * React hook: theme preference + effective theme + setters. Persists to
 * localStorage on change. Listens to OS theme changes when in 'system'.
 */
export function useTheme(): {
  preference: ThemePreference
  effectiveTheme: EffectiveTheme
  setPreference: (p: ThemePreference) => void
  cyclePreference: () => void
} {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => {
    if (typeof window === 'undefined') return 'system'
    return readStoredPreference() ?? 'system'
  })

  const [effectiveTheme, setEffectiveTheme] = useState<EffectiveTheme>(() => {
    if (typeof window === 'undefined') return 'light'
    // Read what's already on <html> (set by initThemeFromStorage), so we
    // stay in sync with the pre-render decision.
    return document.documentElement.classList.contains('dark') ? 'dark' : 'light'
  })

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next)
    const resolved = resolveEffective(next)
    setEffectiveTheme(resolved)
    applyToDOM(resolved)
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // localStorage unavailable (private mode, etc.) — preference still
      // applies for this session, just won't persist. No throw.
    }
  }, [])

  // Cycle: system → light → dark → system. The order puts 'system' first
  // so users who haven't explicitly chosen always see "respect my OS" as
  // the implicit starting point.
  const cyclePreference = useCallback(() => {
    const order: ThemePreference[] = ['system', 'light', 'dark']
    const next = order[(order.indexOf(preference) + 1) % order.length]
    setPreference(next)
  }, [preference, setPreference])

  // Cross-tab sync — if another tab changes the preference, mirror it here.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key !== STORAGE_KEY) return
      const next: ThemePreference =
        e.newValue === 'light' || e.newValue === 'dark' || e.newValue === 'system'
          ? e.newValue
          : 'system'
      setPreferenceState(next)
      const resolved = resolveEffective(next)
      setEffectiveTheme(resolved)
      applyToDOM(resolved)
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // OS theme change — only matters when in 'system' mode. Without this, a
  // user on 'system' who flips their OS to dark mode at night wouldn't see
  // the app follow.
  useEffect(() => {
    if (preference !== 'system' || !window.matchMedia) return
    const mq = window.matchMedia(DARK_MEDIA_QUERY)
    function onChange(e: MediaQueryListEvent) {
      const resolved: EffectiveTheme = e.matches ? 'dark' : 'light'
      setEffectiveTheme(resolved)
      applyToDOM(resolved)
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [preference])

  return { preference, effectiveTheme, setPreference, cyclePreference }
}
